<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>Scadenze imminenti</h1>
          <div class="top-actions">
            <button
              class="create-user-button"
              :class="{ 'create-user-button--active': showExpiredOnly }"
              @click="toggleExpiredOnly"
            >
              <span class="material-icons">hourglass_disabled</span>
              Scaduti
            </button>
            <button
              class="create-user-button create-user-button--stop-filter"
              :class="{ 'create-user-button--active': showStoppedOnly }"
              @click="toggleStoppedOnly"
            >
              <span class="material-icons">block</span>
              STOP
            </button>
            <button
              class="create-user-button create-user-button--refresh staff-button-neutral"
              :disabled="isIndexRefreshing"
              @click="refreshExpiringIndexManual"
            >
              <span class="material-icons" :class="{ 'spin-icon': isIndexRefreshing }">
                {{ isIndexRefreshing ? 'autorenew' : 'sync' }}
              </span>
              {{ isIndexRefreshing ? 'Aggiorno indice...' : 'Refresh indice' }}
            </button>
            <div class="expiring-select">
              <label for="expiring-window">Periodo</label>
              <select id="expiring-window" v-model.number="expiringWindowDays">
                <option :value="7">7 giorni</option>
                <option :value="15">15 giorni</option>
                <option :value="30">30 giorni</option>
                <option :value="60">60 giorni</option>
                <option :value="90">90 giorni</option>
              </select>
            </div>
            <div class="expiring-select">
              <label for="expiring-studio">Studio</label>
              <select id="expiring-studio" v-model="studioFilter">
                <option value="">Tutti</option>
                <option :value="noStudioFilterValue">NO STUDIO</option>
                <option v-for="studio in studioOptions" :key="studio" :value="studio">{{ studio }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="programme-alignment-info">
          <p><strong>3/6 = terzo programma su sei.</strong> Avanzamento e stati seguono le stesse regole del <router-link to="/monitoraggio-percorsi">Monitoraggio percorsi</router-link>.</p>
          <p>La scadenza in questa lista è quella del <strong>pacchetto acquistato</strong>. Il dettaglio dell’avanzamento indica invece la scadenza del programma attuale.</p>
          <p v-if="programProgressUpdatedAt && !programProgressLoading">Avanzamenti verificati: {{ programProgressUpdatedAt.toLocaleString('it-IT') }}.</p>
        </div>

        <div v-if="programProgressLoading" class="programme-progress-loading" role="status" aria-live="polite">
          <span>Controllo del programma attuale e dei rinnovi in coda…</span>
          <div class="programme-progress-track" aria-hidden="true"><span></span></div>
        </div>
        <div v-if="programProgressError" class="programme-progress-error" role="alert">
          <span>{{ programProgressError }}</span>
          <button type="button" :disabled="programProgressLoading" @click="refreshProgramProgress()">Riprova avanzamenti</button>
        </div>

        <div class="loader" v-if="isLoading">
          <h4>CARICAMENTO IN CORSO</h4>
        </div>

        <div class="tickets">
          <table class="users-table">
            <colgroup>
              <col class="email-column" />
              <col class="order-column" />
              <col class="order-date-column" />
              <col class="duration-column" />
              <col class="expiry-column" />
              <col class="program-progress-column" />
              <col class="note-column" />
              <col class="name-column" />
              <col class="surname-column" />
              <col class="studio-column" />
              <col class="actions-column" />
            </colgroup>
            <thead>
              <tr>
                <th class="email-column">Email</th>
                <th class="order-column">Ordine</th>
                <th class="order-date-column">Data ordine pacchetto</th>
                <th class="duration-column">Durata pacchetto (mesi)</th>
                <th class="expiry-column">Scadenza pacchetto</th>
                <th class="program-progress-column">Programma attuale</th>
                <th class="note-column">Note</th>
                <th class="name-column">Nome</th>
                <th class="surname-column">Cognome</th>
                <th class="studio-column">Studio</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in filteredUsers"
                :key="user.uid"
                :class="{
                  'row--stopped': isExpiringListStopped(user),
                  'row--stop-transition': stopTransitionFlags[user.uid]
                }"
              >
                <td class="email-cell">
                  <div class="email-text" :title="user.email">{{ user.email }}</div>
                </td>
                <td class="order-cell">
                  <button
                    type="button"
                    class="order-badge"
                    :class="`order-badge--${orderBadgeStatus(user)}`"
                    @click="showLastOrder(user)"
                    title="Mostra ultimo ordine"
                    aria-label="Mostra ultimo ordine"
                  >
                    <span class="material-icons" aria-hidden="true">receipt_long</span>
                  </button>
                </td>
                <td class="order-date-cell">{{ user._expiringOrderTime ? formatDate(user._expiringOrderTime) : '—' }}</td>
                <td class="duration-cell">{{ user._expiringDurationMonths || '—' }}</td>
                <td class="expiry-cell">{{ user._expiringExpiryMs ? formatDate(user._expiringExpiryMs) : '—' }}</td>
                <td class="program-progress-cell">
                  <div v-for="progress in programProgressEntries(user)" :key="progress.key" class="program-progress-tooltip-wrap" tabindex="0" :aria-label="`${progress.label}: ${progress.summary}`">
                    <span
                      class="program-progress-chip"
                      :class="`program-progress-chip--${progress.state}`"
                    >
                      {{ progress.label }}
                    </span>
                    <small class="program-progress-state">{{ progress.statusLabel }}</small>
                    <div class="program-progress-tooltip" role="tooltip">
                      <strong>{{ progress.summary }}</strong>
                      <span
                        v-for="(line, index) in progress.details"
                        :key="`${progress.key}-detail-${index}`"
                      >
                        {{ line }}
                      </span>
                    </div>
                  </div>
                </td>
                <td class="note-cell">
                  <UserExpiringNote
                    :user-id="user.uid"
                    :user-name="[user.name, user.surname].filter(Boolean).join(' ')"
                    compact
                  />
                </td>
                <td class="name-cell" :title="user.name">{{ user.name }}</td>
                <td class="surname-cell" :title="user.surname">{{ user.surname }}</td>
                <td class="studio-cell" :title="user.studio">{{ user.studio }}</td>
                <td class="actions-cell">
                  <div class="buttons-container">
                    <button
                      class="stop-button"
                      :class="{ 'stop-button--restore': isExpiringListStopped(user) }"
                      :disabled="stopSavingFlags[user.uid] || isUserOperationallyHeld(user)"
                      :title="expiringListStopButtonTitle(user)"
                      :aria-label="expiringListStopButtonTitle(user)"
                      @click="toggleExpiringListStopped(user)"
                    >
                      <span class="material-icons">
                        {{ stopSavingFlags[user.uid] ? 'autorenew' : (isExpiringListStopped(user) ? 'undo' : 'close') }}
                      </span>
                    </button>
                    <span v-if="isUserOperationallyHeld(user)" class="operational-hold-dot" title="Sospeso dal profilo utente">
                      Sospeso
                    </span>
                    <button
                      class="whatsapp-button"
                      :disabled="!hasWhatsAppPhone(user)"
                      :title="whatsAppButtonTitle(user)"
                      :aria-label="whatsAppButtonTitle(user)"
                      @click="openWhatsAppChat(user)"
                    >
                      <svg
                        class="whatsapp-icon"
                        viewBox="0 0 32 32"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          fill="currentColor"
                          d="M16 3C9.37 3 4 8.37 4 15c0 2.04.52 4.03 1.5 5.8L4 29l8.38-1.47A11.9 11.9 0 0 0 16 28c6.63 0 12-5.37 12-12S22.63 3 16 3Zm0 22.2c-1.78 0-3.53-.47-5.05-1.36l-.36-.21-4.05.71.72-3.94-.24-.38A10.12 10.12 0 0 1 5.8 15C5.8 9.37 10.37 4.8 16 4.8S26.2 9.37 26.2 15 21.63 25.2 16 25.2Zm5.62-7.66c-.31-.15-1.83-.9-2.11-1-.28-.1-.49-.15-.69.16-.2.3-.79 1-.97 1.18-.18.2-.36.22-.67.08-.31-.16-1.31-.48-2.49-1.53-.92-.82-1.54-1.84-1.72-2.14-.18-.31-.02-.48.14-.63.14-.14.31-.36.46-.54.15-.18.2-.31.31-.51.1-.2.05-.38-.03-.54-.08-.15-.69-1.66-.95-2.28-.25-.6-.51-.52-.69-.53h-.59c-.2 0-.54.08-.82.39-.28.31-1.08 1.05-1.08 2.56s1.11 2.97 1.26 3.17c.15.2 2.18 3.31 5.28 4.64.74.32 1.31.51 1.76.65.74.23 1.42.2 1.95.12.59-.09 1.83-.74 2.09-1.46.26-.72.26-1.33.18-1.46-.08-.13-.28-.2-.6-.36Z"
                        />
                      </svg>
                    </button>
                    <button @click="viewUserDetails(user.uid)">
                      <span class="material-icons">visibility</span>
                    </button>

                    <button @click="viewUserCommunications(user.uid)"><span class="material-icons">task_alt</span></button>
                    <button @click="viewTechnicalConversation(user.uid)"><span class="material-icons">fitness_center</span></button>
                    <span
                      v-if="user.ticketStatus"
                      :class="{
                        orange: user.ticketStatus === 'Aperto',
                        green: user.ticketStatus === 'Risposto',
                        yellow: user.ticketStatus === 'Non Risposto'
                      }"
                      class="status-dot">
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showCreateConversationModal" class="modal">
    <div class="modal-content">
      <span class="close" @click="closeCreateConversationModal">&times;</span>
      <h2>Apri una nuova conversazione tecnica</h2>

      <form @submit.prevent="createConversation">
        <div>
          <label for="title">Titolo del messaggio:</label>
          <input type="text" v-model="newConversation.title" placeholder="Inserisci il titolo..." />
        </div>

        <div>
          <label for="message">Messaggio:</label>
          <textarea v-model="newConversation.message" placeholder="Scrivi il tuo messaggio..."></textarea>
        </div>

        <button type="button" @click="openDeliveryMessagesModal">Messaggi di consegna</button>

        <button class="staff-button-positive" type="submit" :disabled="isCreatingConversation">{{ isCreatingConversation ? 'Invio…' : 'Crea Conversazione' }}</button>
      </form>
    </div>
  </div>

  <div class="modal" v-if="showDeliveryMessagesModal">
    <div class="modal-content">
      <span class="close" @click="closeDeliveryMessagesModal">&times;</span>
      <h2>Messaggi di Consegna</h2>
      <div class="message-section" v-for="(message, index) in deliveryMessages" :key="index">
        <h3>{{ message.title }}</h3>
        <pre v-html="truncateMessage(message.content, index)"></pre>
        <div class="message-actions">
          <button v-if="!showFullMessage[index]" @click="toggleFullMessage(index)">Mostra tutto</button>
          <button v-else @click="toggleFullMessage(index)">Mostra meno</button>
          <button class="copy-button" @click="copyMessageToConversation(message.content)">Copia nel messaggio</button>
        </div>
      </div>
    </div>
  </div>

  <div class="modal" v-if="showOrderModal">
    <div class="modal-content order-modal">
      <span class="close" @click="closeOrderModal">&times;</span>
      <h2>Ultimo ordine</h2>

      <div v-if="orderLoading" class="order-loader">Caricamento…</div>
      <div v-else-if="orderError" class="order-error">{{ orderError }}</div>
      <div v-else-if="selectedOrder" class="order-card">
        <div class="order-card__body">
          <div class="order-field">
            <span class="order-field__label">Codice</span>
            <span class="order-field__value">{{ orderSummary.code }}</span>
          </div>
          <div class="order-field">
            <span class="order-field__label">Data</span>
            <span class="order-field__value">{{ orderSummary.date }}</span>
          </div>
          <div class="order-field" v-if="orderSummary.status && orderSummary.status !== '—'">
            <span class="order-field__label">Stato</span>
            <span class="order-field__value">{{ orderSummary.status }}</span>
          </div>
          <div class="order-field">
            <span class="order-field__label">Totale</span>
            <span class="order-field__value">{{ orderSummary.total }}</span>
          </div>
          <div class="order-field order-field--products" v-if="orderSummary.products.length">
            <span class="order-field__label">Prodotti</span>
            <ul class="order-products">
              <li
                v-for="(product, index) in orderSummary.products"
                :key="index"
                :class="productHighlightClass(product)"
              >
                {{ product }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div v-else class="order-empty">Nessun ordine trovato per questo utente.</div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, nextTick, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { getFirestore, collection, getDocs, query, where, orderBy, limit, startAfter, documentId, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import SidebarComponent from '../SidebarComponent.vue';
import UserExpiringNote from '@/components/module/UserExpiringNote.vue';
import { useOrderBadge } from '@/composables/useOrderBadge';
import { resolveOrderTimeMs } from '@/lib/topSku';
import { shouldHideFromOperationalLists } from '@/lib/operationalHold';
import { resolveProgrammeProgress } from '@/lib/programmeProgress';
import { createTechnicalTicket, createTechnicalMessageId } from '@/lib/technicalTicketMessages';

const DEFAULT_WINDOW_DAYS = 30;
const MAX_WINDOW_DAYS = 90;
const USER_CHUNK_SIZE = 30;
const ORDER_PAGE_SIZE = 400;
const HIGHLIGHT_PREFETCH_LIMIT = 40;
const TICKET_STATUS_IMMEDIATE_LIMIT = 120;
const TICKET_STATUS_QUERY_CONCURRENCY = 2;
const FIRST_DELIVERY_LOOKUP_BUFFER_DAYS = 45;
const MIN_RENEWAL_TOTAL_EUR = 1;
const ORDER_LOOKBACK_FALLBACK_MONTHS = 18;
const ORDER_LOOKBACK_EXTRA_DAYS = 120;
const USERS_EXPIRING_INDEX_COLLECTION = 'users_expiring_index';
const USERS_EXPIRING_INDEX_META_PATH = ['system_indexes', 'users_expiring_list'];
const NO_STUDIO_FILTER = '__NO_STUDIO__';
const EXPIRING_LIST_NOTE_FIELD = 'expiringListNote';
const EXPIRING_LIST_STOPPED_FIELD = 'expiringListStopped';
const EXPIRING_LIST_STOPPED_AT_FIELD = 'expiringListStoppedAt';
const EXPIRING_LIST_STOPPED_BY_FIELD = 'expiringListStoppedBy';
const STOP_TRANSITION_MS = 2000;
const PROGRAM_PROGRESS_CHUNK_SIZE = USER_CHUNK_SIZE;
const PROGRAM_PROGRESS_INITIAL_PRIORITY_LIMIT = 10;
const PROGRAM_PROGRESS_QUERY_CONCURRENCY = 2;
const STUDIO_OPTIONS = [
  'Tivoli Quartier generale',
  'FF Genova',
  'FF Bologna',
  'FF Bari',
  'FF Pescara',
  'FF Firenze',
  'FF Torino',
  'FF Napoli',
  'FF Milano',
  'FF Roma',
  'FF Salerno',
  'FF Cagliari',
  'FF Milano Città Studi',
  'FF Roma Eur',
  'FF Milano Sempione',
  'FF Padova'
];

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const normalizeSkuKey = (value) => String(value || '').toLowerCase().trim().replace(/[^a-z0-9]/g, '');
const normalizeStatus = (value) => String(value || '').trim().toLowerCase();
const normalizeStudioName = (value) => {
  if (!value) return '';
  return value
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
};
const PAID_ORDER_STATUSES = new Set([
  'pagato',
  'rateizzato',
  'completed',
  'processing',
  'wc-completed',
  'wc-processing',
]);
const PAID_PAYMENT_STATUSES = new Set([
  'completed',
  'paid',
  'captured',
]);

const chunkList = (items, size) => {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
};

const mapWithConcurrency = async (items = [], concurrency = 1, worker) => {
  const source = Array.isArray(items) ? items : [];
  if (!source.length) return [];

  const results = new Array(source.length);
  const workerCount = Math.max(1, Math.min(Number(concurrency) || 1, source.length));
  let nextIndex = 0;
  let stopped = false;

  const runners = Array.from({ length: workerCount }, async () => {
    while (!stopped && nextIndex < source.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      try {
        results[currentIndex] = await worker(source[currentIndex], currentIndex);
      } catch (error) {
        stopped = true;
        throw error;
      }
    }
  });

  await Promise.all(runners);
  return results;
};

const toDateSafe = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const str = String(value).trim();
  if (!str) return null;
  const normalized = str.includes('T') ? str : str.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addMonths = (date, months) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const amount = Number(months);
  if (!Number.isFinite(amount) || amount === 0) return new Date(date.getTime());
  const result = new Date(date.getTime());
  result.setMonth(result.getMonth() + amount);
  return result;
};

const padDatePart = (value) => String(value).padStart(2, '0');

const toOrderDateQueryString = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = padDatePart(date.getMonth() + 1);
  const day = padDatePart(date.getDate());
  const hours = padDatePart(date.getHours());
  const minutes = padDatePart(date.getMinutes());
  const seconds = padDatePart(date.getSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const resolveOrderLookbackStartDate = (productIndex) => {
  const catalogMaxDuration = Number(productIndex?.maxDurationMonths);
  const lookbackMonths = Number.isFinite(catalogMaxDuration) && catalogMaxDuration > 0
    ? Math.max(ORDER_LOOKBACK_FALLBACK_MONTHS, catalogMaxDuration + 2)
    : ORDER_LOOKBACK_FALLBACK_MONTHS;
  const start = addMonths(new Date(), -lookbackMonths) || new Date();
  start.setDate(start.getDate() - ORDER_LOOKBACK_EXTRA_DAYS);
  return start;
};

const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const raw = String(value).trim();
  if (!raw) return NaN;
  let normalized = raw.replace(/[^\d.,-]/g, '');
  if (!normalized) return NaN;

  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');
  if (hasComma && hasDot) {
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (hasComma) {
    normalized = normalized.replace(',', '.');
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const resolveLineTotal = (line = {}) => {
  const explicit = parseAmount(line.total ?? line.line_total ?? line.total_price ?? line.lineTotal);
  if (Number.isFinite(explicit)) return explicit;
  const qty = Number(line.quantity ?? line.qty ?? 1);
  const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 1;
  const unitPrice = parseAmount(line.price ?? line.unitPrice ?? line.unit_price ?? line.amount ?? 0);
  if (Number.isFinite(unitPrice)) return unitPrice * safeQty;
  return NaN;
};

const resolveOrderTotal = (order = {}) => {
  const directFields = [
    order.totalPrice,
    order.total,
    order.totalAmount,
    order.orderTotal,
    order.amount,
    order.paymentAmount,
    order.grandTotal,
  ];
  for (const candidate of directFields) {
    const parsed = parseAmount(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }

  const products = Array.isArray(order.products) ? order.products : [];
  const lineItems = Array.isArray(order.line_items) ? order.line_items : [];
  const allLines = [...products, ...lineItems];
  let sum = 0;
  let hasLineTotal = false;
  allLines.forEach((line) => {
    const lineTotal = resolveLineTotal(line);
    if (!Number.isFinite(lineTotal)) return;
    sum += lineTotal;
    hasLineTotal = true;
  });
  return hasLineTotal ? sum : NaN;
};

const hasOrderValueAboveMinimum = (order = {}) => {
  const total = resolveOrderTotal(order);
  return Number.isFinite(total) && total > MIN_RENEWAL_TOTAL_EUR;
};

const isInvalidOrderStatus = (statusRaw) => {
  const status = normalizeStatus(statusRaw);
  if (!status) return false;
  return (
    status.includes('rimborsato') ||
    status.includes('cancellato') ||
    status.includes('scaduto') ||
    status.includes('refunded') ||
    status.includes('void') ||
    status.includes('expired') ||
    status.includes('refused')
  );
};

const isOrderPaidStrict = (order = {}) => {
  const statusRaw = normalizeStatus(order.status || order.orderStatus);
  const paymentStatus = normalizeStatus(order.paymentStatus || order.payment_status);

  if (isInvalidOrderStatus(statusRaw) || isInvalidOrderStatus(paymentStatus)) return false;
  if (PAID_ORDER_STATUSES.has(statusRaw)) return true;
  if (PAID_PAYMENT_STATUSES.has(paymentStatus)) return true;
  if (paymentStatus.includes('completed') || paymentStatus.includes('captured')) return true;

  return false;
};

const buildProductsIndex = async (db) => {
  const byId = new Map();
  const bySku = new Map();
  let maxDurationMonths = 0;
  const snap = await getDocs(collection(db, 'products'));
  snap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const idKey = String(docSnap.id || '').trim();
    if (idKey) byId.set(idKey, data);
    const skuCandidate = data.sku || data.productSku || data.product_id || data.productId;
    const skuKey = normalizeSkuKey(skuCandidate);
    if (skuKey) bySku.set(skuKey, data);
    const programCountRaw = Number(data.program_count ?? data.programCount ?? 0);
    const programCount = Number.isFinite(programCountRaw) && programCountRaw > 0 ? programCountRaw : 0;
    const durationRaw = Number(data.duration_months ?? data.durationMonths ?? 0);
    if (programCount > 0 && Number.isFinite(durationRaw) && durationRaw > maxDurationMonths) {
      maxDurationMonths = durationRaw;
    }
  });
  return { byId, bySku, maxDurationMonths };
};

const resolveCatalogProduct = (line = {}, productIndex) => {
  if (!productIndex) return null;
  const { byId, bySku } = productIndex;
  const idCandidates = [
    line.id,
    line.productId,
    line.product_id,
    line.catalogId,
    line.itemId,
  ];
  for (const candidate of idCandidates) {
    const key = String(candidate || '').trim();
    if (key && byId.has(key)) return byId.get(key);
  }
  const skuCandidates = [line.sku, line.SKU, line.productSku, line.product_sku];
  for (const candidate of skuCandidates) {
    const key = normalizeSkuKey(candidate);
    if (key && bySku.has(key)) return bySku.get(key);
  }
  return null;
};

const resolveLineProgramCount = (line = {}, productIndex) => {
  const product = resolveCatalogProduct(line, productIndex);
  const raw =
    product?.program_count ??
    product?.programCount ??
    line?.program_count ??
    line?.programCount;
  const count = Number(raw);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

const resolveLineDurationMonths = (line = {}, productIndex) => {
  const product = resolveCatalogProduct(line, productIndex);
  const raw =
    product?.duration_months ??
    product?.durationMonths ??
    line?.duration_months ??
    line?.durationMonths ??
    line?.duration;
  const months = Number(raw);
  return Number.isFinite(months) && months > 0 ? months : 0;
};

const collectOrderKeys = (order = {}) => {
  const emails = [
    order.userEmail,
    order.customerEmail,
    order.email,
    order.customer_email,
    order.billing?.email,
  ]
    .map((value) => normalizeEmail(value))
    .filter(Boolean);

  const uids = [order.userUid, order.uid, order.userId]
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  const uniqueEmails = Array.from(new Set(emails));
  const uniqueUids = Array.from(new Set(uids));
  const identityKeys = [
    ...uniqueUids.map((uid) => `uid:${uid}`),
    ...uniqueEmails.map((email) => `email:${email}`),
  ];

  return {
    emails: uniqueEmails,
    uids: uniqueUids,
    identityKeys,
  };
};

const collectOrderRefs = (order = {}) =>
  Array.from(
    new Set(
      [order.orderId, order._id, order.id]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )
  );

const resolveDurationMonthsForOrder = (order = {}, productIndex) => {
  const products = Array.isArray(order.products) ? order.products : [];
  const lineItems = Array.isArray(order.line_items) ? order.line_items : [];
  const allLines = [...products, ...lineItems];
  let durationMonths = 0;

  allLines.forEach((line) => {
    const programCount = resolveLineProgramCount(line, productIndex);
    if (programCount <= 0) return;
    const lineDuration = resolveLineDurationMonths(line, productIndex);
    if (lineDuration <= 0) return;
    if (lineDuration > durationMonths) {
      durationMonths = lineDuration;
    }
  });

  return durationMonths || 0;
};

const buildValidOrderCandidate = (order = {}, productIndex) => {
  if (!isOrderPaidStrict(order)) return null;
  if (!hasOrderValueAboveMinimum(order)) return null;

  const orderTime = resolveOrderTimeMs(order);
  if (!orderTime) return null;

  const durationMonths = resolveDurationMonthsForOrder(order, productIndex);
  if (!durationMonths) return null;

  const keys = collectOrderKeys(order);
  if (!keys.identityKeys.length) return null;

  return {
    orderTime,
    durationMonths,
    emails: keys.emails,
    uids: keys.uids,
    identityKeys: keys.identityKeys,
    orderRefs: collectOrderRefs(order),
  };
};

const buildOrderIdentityStats = (orders = []) => {
  const size = Array.isArray(orders) ? orders.length : 0;
  if (!size) {
    return {
      firstOrderIndexes: new Set(),
      rootByIndex: [],
      orderCountByRoot: new Map(),
      maxOrderTimeByRoot: new Map(),
    };
  }

  const parent = Array.from({ length: size }, (_, idx) => idx);
  const rank = Array.from({ length: size }, () => 0);

  const find = (idx) => {
    if (parent[idx] !== idx) parent[idx] = find(parent[idx]);
    return parent[idx];
  };

  const union = (a, b) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA === rootB) return;
    if (rank[rootA] < rank[rootB]) {
      parent[rootA] = rootB;
    } else if (rank[rootA] > rank[rootB]) {
      parent[rootB] = rootA;
    } else {
      parent[rootB] = rootA;
      rank[rootA] += 1;
    }
  };

  const seenByKey = new Map();
  orders.forEach((order, idx) => {
    const keys = Array.isArray(order?.identityKeys) ? order.identityKeys : [];
    keys.forEach((key) => {
      if (!key) return;
      const previousIdx = seenByKey.get(key);
      if (previousIdx === undefined) {
        seenByKey.set(key, idx);
      } else {
        union(idx, previousIdx);
      }
    });
  });

  const rootByIndex = orders.map((_, idx) => find(idx));
  const firstByRoot = new Map();
  const orderCountByRoot = new Map();
  const maxOrderTimeByRoot = new Map();
  rootByIndex.forEach((root, idx) => {
    const order = orders[idx] || {};
    const currentFirst = firstByRoot.get(root);
    if (currentFirst === undefined) {
      firstByRoot.set(root, idx);
    } else {
      const currentFirstTime = Number(orders[currentFirst]?.orderTime) || 0;
      const candidateTime = Number(order?.orderTime) || 0;
      if (candidateTime < currentFirstTime || (candidateTime === currentFirstTime && idx < currentFirst)) {
        firstByRoot.set(root, idx);
      }
    }

    const count = orderCountByRoot.get(root) || 0;
    orderCountByRoot.set(root, count + 1);

    const orderTime = Number(order?.orderTime) || 0;
    const maxTime = maxOrderTimeByRoot.get(root);
    if (maxTime === undefined || orderTime > maxTime) {
      maxOrderTimeByRoot.set(root, orderTime);
    }
  });

  return {
    firstOrderIndexes: new Set(firstByRoot.values()),
    rootByIndex,
    orderCountByRoot,
    maxOrderTimeByRoot,
  };
};

const extractFirstDeliveryDoneAtMs = (steps = []) => {
  if (!Array.isArray(steps) || !steps.length) return 0;
  let minMs = 0;
  steps.forEach((step) => {
    const name = String(step?.name || '').toLowerCase();
    if (!name.includes('consegna')) return;
    if (step?.done !== true && !step?.doneAt) return;
    const parsed = toDateSafe(step?.doneAt);
    if (!parsed) return;
    const ms = parsed.getTime();
    if (!Number.isFinite(ms)) return;
    if (!minMs || ms < minMs) minMs = ms;
  });
  return minMs || 0;
};

const fetchFirstDeliveryDoneAtByOrderRef = async (db, orderRefs = []) => {
  const refs = Array.from(
    new Set(
      (Array.isArray(orderRefs) ? orderRefs : [])
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )
  );
  const map = new Map();
  if (!refs.length) return map;

  const chunks = chunkList(refs, USER_CHUNK_SIZE);
  const snapshots = await Promise.all(chunks.map((chunk) =>
    getDocs(
      query(collection(db, 'percorsi'), where('orderRef', 'in', chunk))
    )
  ));
  snapshots.forEach((snap) => {
    snap.forEach((docSnap) => {
      const data = docSnap.data() || {};
      const orderRef = String(data.orderRef || '').trim();
      if (!orderRef) return;
      const doneAtMs = extractFirstDeliveryDoneAtMs(data.steps);
      if (!doneAtMs) return;
      const current = map.get(orderRef) || 0;
      if (!current || doneAtMs < current) {
        map.set(orderRef, doneAtMs);
      }
    });
  });

  return map;
};

const resolveFirstDeliveryForOrder = (order = {}, firstDeliveryByRef = new Map()) => {
  const refs = Array.isArray(order.orderRefs) ? order.orderRefs : [];
  let minMs = 0;
  refs.forEach((ref) => {
    const value = firstDeliveryByRef.get(ref) || 0;
    if (!value) return;
    if (!minMs || value < minMs) minMs = value;
  });
  return minMs || 0;
};

const resolveRawPhone = (user = {}) => {
  const candidates = [
    user.phone,
    user.telefono,
    user.mobile,
    user.cellulare,
  ];
  const value = candidates.find((entry) => String(entry || '').trim());
  return String(value || '').trim();
};

const normalizeWhatsAppPhone = (rawPhone = '') => {
  let digits = String(rawPhone || '').trim();
  if (!digits) return '';
  digits = digits.replace(/^00/, '');
  digits = digits.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10 && digits.startsWith('3')) {
    return `39${digits}`;
  }
  return digits;
};

const WHATSAPP_WEB_WINDOW_NAME = 'fuscoFitWhatsAppWeb';
let whatsappWebWindowRef = null;

const openWhatsAppWebWindow = (url) => {
  if (whatsappWebWindowRef && !whatsappWebWindowRef.closed) {
    try {
      whatsappWebWindowRef.location = url;
      whatsappWebWindowRef.focus();
      return;
    } catch (error) {
      console.warn('Impossibile riusare la finestra WhatsApp Web:', error);
    }
  }

  whatsappWebWindowRef = window.open(url, WHATSAPP_WEB_WINDOW_NAME);
  if (whatsappWebWindowRef) {
    whatsappWebWindowRef.focus();
  }
};


export default {
  components: {
    SidebarComponent,
    UserExpiringNote,
  },
  setup() {
    const users = ref([]);
    const isLoading = ref(false);
    const showExpiredOnly = ref(false);
    const showStoppedOnly = ref(false);
    const expiringWindowDays = ref(DEFAULT_WINDOW_DAYS);
    const studioFilter = ref('');
    const stopSavingFlags = reactive({});
    const stopTransitionFlags = reactive({});
    const stopTransitionTimers = new Map();
    let programProgressGeneration = 0;
    const programProgressLoading = ref(false);
    const programProgressError = ref('');
    const programProgressUpdatedAt = ref(null);
    const showCreateConversationModal = ref(false);
    const newConversation = ref({ title: '', message: '' });
    const isCreatingConversation = ref(false);
    let pendingConversation = null;
    const showDeliveryMessagesModal = ref(false);
    const showFullMessage = ref([]);
    const deliveryMessages = ref([
      {
        title: 'MESSAGGIO POST ACQUISTO - PROGRAMMA COMPLETO:',
        content: `Ciaooo\n\nbenvenuta in Fusco Fit! 🎉\n\nstiamo per costruire insieme il tuo percorso personalizzato.\n\nEcco come funziona, passo dopo passo:\n\n📋 Prossimi Step\n\n<strong>1. Scarica la nostra app.</strong>\n📲 Fusco Fit Connect (logo nero) → comunicazioni, assistenza, piano alimentare e allenamenti sono tutti nella stessa app.\n🔍 Cerca “Fusco Fit Connect” nello store del tuo telefono e scaricala.\n📝 Registrati usando la stessa email che ci hai fornito all’acquisto.\n\n<strong>2. Compila il questionario che riceverai via email (se non lo hai già compilato).</strong>\n📧 È indispensabile per costruire il tuo piano.\n🖊️ Più sarai precisa (soprattutto nelle note libere), più riusciremo a personalizzare il tuo percorso.\n📬 Dopo l’invio, potresti ricevere via mail una richiesta di approfondimento: controlla spesso la posta.\n\n<strong>3. Video call con la nutrizionista</strong>\nDopo che il tuo questionario sarà approvato, verrai contattata per fissare l’appuntamento in videocall, se non riesci a rispondere richiamaci appena puoi.\nLa call è indispensabile per perfezionare il piano alimentare e per ricevere l’allenamento.\n\n<strong>4. Se il tuo piano prevede una call tecnica (servizio plus)</strong>\nRiceverai un messaggio in app Fusco Fit Connect (logo nero) per fissare la videocall.\n📅 In quel momento ti chiederemo di indicarci le tue fasce orarie di disponibilità per prenotarla, chiaramente dopo la consegna delle programmazioni.\n\n<strong>5. Consegna del programma</strong>\nNei 2 giorni lavorativi successivi alla call con la nutrizionista prepareremo il tuo piano.\n📲 La consegna sarà pronta quando riceverai il messaggio di conferma in Fusco Fit Connect (logo nero).\n📂 Il piano alimentare sarà nella sezione Alimentazione.\n💪 Il programma di allenamento sarà nella sezione Allenamenti della stessa app.\n\n<strong>➡️ Dopo la consegna</strong>\nPotrai iniziare subito a seguire il tuo percorso.\nPer dubbi o chiarimenti, scrivici illimitatamente su “assistenza tecnica” su app Fusco Fit Connect (logo nero), mentre per dubbi alimentari avrai a disposizione 2 ticket al mese su “assistenza nutrizionale”.\n\n<strong>📌 Nota importante sulle call tecniche</strong>\nLa call tecnica può essere utilizzata solo all’interno del programma in corso.\nEsempio: se hai acquistato 6 programmi → avrai diritto a 1 call per ogni programma.\n👉 Una volta terminato il singolo programma, anche la call associata scade se non è stata utilizzata.\nPrima di effettuare la videocall segnati ogni dubbio che hai sulla scheda, così li analizzeremo INSIEME❤️\n\n<strong>💪🏻Orari assistenza💪🏻</strong>\nIl nostro team è disponibile:\n🕘 Lunedì – Venerdì dalle 9:00 alle 19:00\n🕙 Sabato dalle 10:00 alle 13:00`
      },
      {
        title: 'MESSAGGIO POST ACQUISTO - SOLO ALLENAMENTO:',
        content: `Ciaooo\n\nbenvenuta in Fusco Fit! 🎉\n\nstiamo per costruire insieme il tuo percorso personalizzato.\n\nEcco come funziona, passo dopo passo:\n\n📋 Prossimi Step\n\n<strong>1. Scarica la nostra app.</strong>\n📲 Fusco Fit Connect (logo nero) → comunicazioni, assistenza e allenamenti sono tutti nella stessa app.\n🔍 Cerca “Fusco Fit Connect” nello store del tuo telefono e scaricala.\n📝 Registrati usando la stessa email che ci hai fornito all’acquisto.\n\n<strong>2.Compila il questionario che riceverai via email (se non lo hai già compilato).</strong>\n📧 È indispensabile per costruire il tuo piano.\n🖊️ Più sarai precisa (soprattutto nelle note libere), più riusciremo a personalizzare il tuo percorso.\n📬 Dopo l’invio, potresti ricevere via mail una richiesta di approfondimento: controlla spesso la posta.\n\n<strong>3.Se il tuo piano prevede una call tecnica (servizio plus)</strong>\nRiceverai un messaggio in app Fusco Fit Connect (logo nero) per fissare la videocall.\n📅 In quel momento ti chiederemo di indicarci le tue fasce orarie di disponibilità per prenotarla, chiaramente dopo la consegna delle programmazioni.\n\n<strong>4.Consegna del programma</strong>\n📲 La consegna sarà pronta quando riceverai il messaggio di conferma in Fusco Fit Connect (logo nero).\n💪 Il tuo programma di allenamento sarà caricato nella sezione Allenamenti della stessa app.\n\n<strong>➡️ Dopo la consegna</strong>\nPotrai iniziare subito a seguire il tuo percorso.\nPer dubbi o chiarimenti, scrivici illimitatamente su “assistenza tecnica” su app Fusco Fit Connect (logo nero)\n\n<strong>📌 Nota importante sulle call tecniche</strong>\nLa call tecnica può essere utilizzata solo all’interno del programma in corso.\nEsempio: se hai acquistato 6 programmi → avrai diritto a 1 call per ogni programma.\n👉 Una volta terminato il singolo programma, anche la call associata scade se non è stata utilizzata.\nPrima di effettuare la videocall segnati ogni dubbio che hai sulla scheda, così li analizzeremo INSIEME❤️\n\n<strong>💪🏻Orari assistenza💪🏻</strong>\nIl nostro team è disponibile:\n🕘 Lunedì – Venerdì dalle 9:00 alle 19:00\n🕙 Sabato dalle 10:00 alle 13:00`
      },
      {
        title: 'MESSAGGIO POST ACQUISTO - SOLO DIETA:',
        content: `Ciaooooo\n\nbenvenuta in Fusco Fit! 🎉\n\nstiamo per costruire insieme il tuo percorso personalizzato.\n\nEcco come funziona, passo dopo passo:\n\n📋 Prossimi Step\n\n<strong>1. Scarica la nostra app.</strong>\n📲 Fusco Fit Connect (logo nero) → utile per le comunicazioni, assistenza e piano alimentare.\n🔍 Cerca “Fusco Fit Connect” nello store del tuo telefono e scaricala.\n📝 Registrati usando la stessa email che ci hai fornito all’acquisto.\n\n<strong>2. Compila il questionario che riceverai via email (se non lo hai già compilato).</strong>\n📧 È indispensabile per costruire il tuo piano.\n🖊️ Più sarai precisa (soprattutto nelle note libere), più riusciremo a personalizzare il tuo percorso.\n📬 Dopo l’invio, potresti ricevere via mail una richiesta di approfondimento: controlla spesso la posta.\n\n<strong>3. Video call con la nutrizionista</strong>\nDopo che il tuo questionario sarà approvato, verrai contattata per fissare l’appuntamento in videocall, se non riesci a rispondere richiamaci appena puoi.\nLa call è indispensabile per perfezionare il piano alimentare.\n\n<strong>5. Consegna del programma</strong>\nNei 2 giorni lavorativi successivi alla call con la nutrizionista prepareremo il tuo piano.\n📲 La consegna sarà pronta quando riceverai il messaggio di conferma in app Fusco Fit Connect (logo nero).\n📂 Il tuo piano alimentare sarà nell’app Fusco Fit Connect (logo nero).\n\n<strong>➡️ Dopo la consegna</strong>\nPotrai iniziare subito a seguire il tuo percorso.\nPer dubbi o chiarimenti, scrivici illimitatamente su “assistenza tecnica” su app Fusco Fit Connect (logo nero), mentre per dubbi alimentari avrai a disposizione 2 ticket al mese su “assistenza nutrizionale”.\n\n<strong>💪🏻Orari assistenza💪🏻</strong>\nIl nostro team è disponibile:\n🕘 Lunedì – Venerdì dalle 9:00 alle 19:00\n🕙 Sabato dalle 10:00 alle 13:00`
      },
    ]);

    const selectedUserId = ref(null);

    const {
      showOrderModal,
      orderLoading,
      orderError,
      selectedOrder,
      orderSummary,
      openLastOrder,
      closeOrderModal,
      orderBadgeStatus,
      productHighlightClass,
      prefetchUserHighlights,
    } = useOrderBadge();

    const showLastOrder = openLastOrder;

    const db = getFirestore();
    const functions = getFunctions();
    const router = useRouter();
    const isIndexRefreshing = ref(false);

    const toggleExpiredOnly = () => {
      showExpiredOnly.value = !showExpiredOnly.value;
    };

    const toggleStoppedOnly = () => {
      showStoppedOnly.value = !showStoppedOnly.value;
    };

    onUnmounted(() => {
      programProgressGeneration += 1;
      stopTransitionTimers.forEach((timerId) => window.clearTimeout(timerId));
      stopTransitionTimers.clear();
    });

    const scheduleLazyTask = (task) => {
      if (typeof task !== 'function') return;
      const run = () => {
        Promise.resolve()
          .then(task)
          .catch((error) => console.warn('Task asincrono fallito:', error));
      };
      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 1200 });
        return;
      }
      setTimeout(run, 0);
    };

    const updateUserLocally = (userId, patch = {}) => {
      if (!userId) return;
      users.value = users.value.map((entry) =>
        entry.uid === userId
          ? {
              ...entry,
              ...patch,
            }
          : entry
      );
    };

    const isUserOperationallyHeld = (user) => shouldHideFromOperationalLists(user || {});
    const isExpiringListStopped = (user) =>
      user?.[EXPIRING_LIST_STOPPED_FIELD] === true || isUserOperationallyHeld(user);

    const expiringListStopButtonTitle = (user) =>
      isUserOperationallyHeld(user)
        ? 'Sospeso dal profilo utente'
        : isExpiringListStopped(user)
        ? 'Riattiva nella lista scadenze'
        : 'Metti in STOP e nascondi dalla lista';

    const setStopTransition = (userId) => {
      if (!userId) return;
      const currentTimer = stopTransitionTimers.get(userId);
      if (currentTimer) {
        window.clearTimeout(currentTimer);
      }
      stopTransitionFlags[userId] = true;
      const timerId = window.setTimeout(() => {
        delete stopTransitionFlags[userId];
        stopTransitionTimers.delete(userId);
      }, STOP_TRANSITION_MS);
      stopTransitionTimers.set(userId, timerId);
    };

    const clearStopTransition = (userId) => {
      if (!userId) return;
      const currentTimer = stopTransitionTimers.get(userId);
      if (currentTimer) {
        window.clearTimeout(currentTimer);
      }
      stopTransitionTimers.delete(userId);
      delete stopTransitionFlags[userId];
    };

    const toggleExpiringListStopped = async (user) => {
      const userId = String(user?.uid || '').trim();
      if (!userId || stopSavingFlags[userId]) return;

      const currentUser = users.value.find((entry) => entry.uid === userId) || user;
      const nextStopped = !isExpiringListStopped(currentUser);
      const previousState = {
        [EXPIRING_LIST_STOPPED_FIELD]: currentUser?.[EXPIRING_LIST_STOPPED_FIELD] === true,
        [EXPIRING_LIST_STOPPED_AT_FIELD]: currentUser?.[EXPIRING_LIST_STOPPED_AT_FIELD] || null,
        [EXPIRING_LIST_STOPPED_BY_FIELD]: currentUser?.[EXPIRING_LIST_STOPPED_BY_FIELD] || null,
      };

      setStopTransition(userId);
      stopSavingFlags[userId] = true;

      const auth = getAuth();
      const operatorId = auth.currentUser ? auth.currentUser.uid : null;
      const stoppedAt = nextStopped ? new Date().toISOString() : null;
      const stoppedBy = nextStopped ? operatorId : null;
      const localPatch = {
        [EXPIRING_LIST_STOPPED_FIELD]: nextStopped,
        [EXPIRING_LIST_STOPPED_AT_FIELD]: stoppedAt,
        [EXPIRING_LIST_STOPPED_BY_FIELD]: stoppedBy,
      };
      updateUserLocally(userId, localPatch);

      try {
        await updateDoc(doc(db, 'users', userId), {
          ...localPatch,
          updatedAt: new Date().toISOString(),
        });
      } catch (error) {
        console.error('Errore durante il salvataggio dello STOP scadenze:', error);
        updateUserLocally(userId, previousState);
        clearStopTransition(userId);
        alert('Errore durante il salvataggio dello STOP.');
      } finally {
        stopSavingFlags[userId] = false;
      }
    };

    const hydrateExpiringListUserFields = async (userList = []) => {
      const userIds = Array.from(
        new Set(
          userList
            .map((user) => String(user?.uid || '').trim())
            .filter(Boolean)
        )
      );
      if (!userIds.length) return userList;

      const fieldsByUid = {};
      const chunks = chunkList(userIds, USER_CHUNK_SIZE);
      const snapshots = await Promise.all(chunks.map((chunk) =>
        getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)))
      ));

      snapshots.forEach((snap) => {
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          fieldsByUid[docSnap.id] = {
            note: String(data[EXPIRING_LIST_NOTE_FIELD] || ''),
            phone: resolveRawPhone(data),
            stopped: data[EXPIRING_LIST_STOPPED_FIELD] === true,
            stoppedAt: data[EXPIRING_LIST_STOPPED_AT_FIELD] || null,
            stoppedBy: data[EXPIRING_LIST_STOPPED_BY_FIELD] || null,
            operationalHold: data.operationalHold || null,
          };
        });
      });

      return userList.map((user) => {
        const fields = fieldsByUid[user.uid];
        if (!fields) return user;
        const nextUser = { ...user };
        if (fields.phone) {
          nextUser.phone = fields.phone;
        }
        nextUser[EXPIRING_LIST_NOTE_FIELD] = fields.note;
        if (!stopSavingFlags[user.uid] && !stopTransitionFlags[user.uid]) {
          nextUser[EXPIRING_LIST_STOPPED_FIELD] = fields.stopped;
          nextUser[EXPIRING_LIST_STOPPED_AT_FIELD] = fields.stoppedAt;
          nextUser[EXPIRING_LIST_STOPPED_BY_FIELD] = fields.stoppedBy;
        }
        nextUser.operationalHold = fields.operationalHold || null;
        nextUser._expiringListNoteNeedsFetch = false;
        return nextUser;
      });
    };

    const loadExpiringListNotes = async (userList = []) => {
      const hydratedUsers = await hydrateExpiringListUserFields(userList);
      const hydratedByUid = new Map(hydratedUsers.map((user) => [user.uid, user]));

      users.value = users.value.map((user) => {
        const hydratedUser = hydratedByUid.get(user.uid);
        if (!hydratedUser) return user;
        return {
          ...user,
          phone: hydratedUser.phone,
          [EXPIRING_LIST_NOTE_FIELD]: hydratedUser[EXPIRING_LIST_NOTE_FIELD],
          [EXPIRING_LIST_STOPPED_FIELD]: hydratedUser[EXPIRING_LIST_STOPPED_FIELD],
          [EXPIRING_LIST_STOPPED_AT_FIELD]: hydratedUser[EXPIRING_LIST_STOPPED_AT_FIELD],
          [EXPIRING_LIST_STOPPED_BY_FIELD]: hydratedUser[EXPIRING_LIST_STOPPED_BY_FIELD],
          operationalHold: hydratedUser.operationalHold || null,
          _expiringListNoteNeedsFetch: false,
        };
      });
    };

    const loadProgramProgress = async (
      userList = [],
      priorityUserIds = [],
      generation = programProgressGeneration
    ) => {
      const userIds = Array.from(
        new Set(
          userList
            .map((user) => String(user?.uid || '').trim())
            .filter(Boolean)
        )
      );
      if (!userIds.length) return;

      const isCurrentGeneration = () => generation === programProgressGeneration;
      const productsSnapshot = await getDocs(collection(db, 'products'));
      if (!isCurrentGeneration()) return;
      const products = productsSnapshot.docs.map(item => ({ ...item.data(), id: item.id }));
      const usersById = new Map(userList.map(user => [user.uid, user]));
      const readAt = new Date();
      const resolveProgress = (uid, percorsi) => resolveProgrammeProgress({
        uid, percorsi, products, user: usersById.get(uid), now: readAt,
      });
      const userIdSet = new Set(userIds);
      const priorityIds = Array.from(
        new Set(
          (Array.isArray(priorityUserIds) ? priorityUserIds : [])
            .map((uid) => String(uid || '').trim())
            .filter((uid) => uid && userIdSet.has(uid))
        )
      );
      const priorityIdSet = new Set(priorityIds);
      const initialPriorityIds = priorityIds.slice(0, PROGRAM_PROGRESS_INITIAL_PRIORITY_LIMIT);
      const secondaryPriorityIds = priorityIds.slice(PROGRAM_PROGRESS_INITIAL_PRIORITY_LIMIT);
      const remainingIds = userIds.filter((uid) => !priorityIdSet.has(uid));

      const percorsiByUid = new Map();
      userIds.forEach((uid) => percorsiByUid.set(uid, []));
      const seenPercorsoIds = new Set();

      const mergeResults = (results = []) => {
        results.forEach((snapshotPair) => {
          if (!snapshotPair) return;
          snapshotPair.forEach((snapshot) => {
            snapshot.forEach((docSnap) => {
              if (seenPercorsoIds.has(docSnap.id)) return;
              seenPercorsoIds.add(docSnap.id);
              const data = docSnap.data() || {};
              const uid = String(data.uid || data.userUid || '').trim();
              if (!uid) return;
              const rows = percorsiByUid.get(uid) || [];
              rows.push({ ...data, id: docSnap.id });
              percorsiByUid.set(uid, rows);
            });
          });
        });
      };

      const applyProgressByUid = (progressByUid = new Map()) => {
        if (!progressByUid.size || !isCurrentGeneration()) return;
        users.value = users.value.map((user) => {
          if (!progressByUid.has(user.uid)) return user;
          return {
            ...user,
            _programProgress: progressByUid.get(user.uid),
          };
        });
      };

      const applySnapshotPairProgress = (ids = [], snapshotPair = []) => {
        if (!ids.length || !snapshotPair || !isCurrentGeneration()) return;
        const rowsByUid = new Map(ids.map((uid) => [uid, []]));
        const localSeenPercorsoIds = new Set();

        snapshotPair.forEach((snapshot) => {
          snapshot.forEach((docSnap) => {
            if (localSeenPercorsoIds.has(docSnap.id)) return;
            localSeenPercorsoIds.add(docSnap.id);
            const data = docSnap.data() || {};
            const uid = String(data.uid || data.userUid || '').trim();
            if (!rowsByUid.has(uid)) return;
            rowsByUid.get(uid).push({ ...data, id: docSnap.id });
          });
        });

        const progressByUid = new Map();
        ids.forEach((uid) => {
          progressByUid.set(uid, resolveProgress(uid, rowsByUid.get(uid) || []));
        });
        applyProgressByUid(progressByUid);
      };

      const fetchIds = async (ids = [], progressive = false) => {
        if (!ids.length) return isCurrentGeneration();

        const results = await mapWithConcurrency(
          chunkList(ids, PROGRAM_PROGRESS_CHUNK_SIZE),
          PROGRAM_PROGRESS_QUERY_CONCURRENCY,
          async (chunk) => {
            if (!isCurrentGeneration()) return null;
            const snapshotPair = await Promise.all([
              getDocs(query(collection(db, 'percorsi'), where('uid', 'in', chunk))),
              getDocs(query(collection(db, 'percorsi'), where('userUid', 'in', chunk))),
            ]);
            if (progressive) {
              applySnapshotPairProgress(chunk, snapshotPair);
            }
            return snapshotPair;
          }
        );

        if (!isCurrentGeneration()) return false;
        mergeResults(results);
        return true;
      };

      const applyProgress = (ids = []) => {
        if (!ids.length || !isCurrentGeneration()) return;
        const progressByUid = new Map();
        ids.forEach((uid) => {
          progressByUid.set(uid, resolveProgress(uid, percorsiByUid.get(uid) || []));
        });
        applyProgressByUid(progressByUid);
      };

      if (initialPriorityIds.length) {
        const initialPriorityLoaded = await fetchIds(initialPriorityIds);
        if (!initialPriorityLoaded) return;
        applyProgress(initialPriorityIds);
      }

      if (secondaryPriorityIds.length) {
        const secondaryPriorityLoaded = await fetchIds(secondaryPriorityIds, true);
        if (!secondaryPriorityLoaded) return;
        // Riconcilia la prima fascia con il resto delle righe visibili.
        applyProgress(priorityIds);
      }

      if (remainingIds.length) {
        const remainingLoaded = await fetchIds(remainingIds);
        if (!remainingLoaded) return;
        // Riconcilia tutti gli utenti per includere anche eventuali record legacy incrociati.
        applyProgress(userIds);
      }
    };

    const refreshProgramProgress = async (generation = programProgressGeneration) => {
      if (generation !== programProgressGeneration) return;
      programProgressLoading.value = true;
      programProgressError.value = '';
      try {
        await loadProgramProgress(users.value, filteredUsers.value.map(user => user.uid), generation);
        if (generation === programProgressGeneration) programProgressUpdatedAt.value = new Date();
      } catch (error) {
        if (generation !== programProgressGeneration) return;
        console.warn('Caricamento dettaglio programmi fallito:', error);
        programProgressError.value = 'Non è stato possibile aggiornare tutti gli avanzamenti. Le posizioni non caricate restano da verificare.';
      } finally {
        if (generation === programProgressGeneration) programProgressLoading.value = false;
      }
    };

    const loadTicketStatuses = async (
      userList = [],
      generation = programProgressGeneration
    ) => {
      const isCurrentGeneration = () => generation === programProgressGeneration;
      const ticketsCollection = collection(db, 'assistenzatecnica');
      const userIds = userList.map((user) => user.uid).filter(Boolean);
      if (!userIds.length) return isCurrentGeneration();

      const ticketStatuses = {};
      const chunks = chunkList(userIds, USER_CHUNK_SIZE);
      const snapshots = await mapWithConcurrency(
        chunks,
        TICKET_STATUS_QUERY_CONCURRENCY,
        (chunk) => {
          if (!isCurrentGeneration()) return null;
          return getDocs(query(ticketsCollection, where('userId', 'in', chunk)));
        }
      );
      snapshots.forEach((querySnapshot) => {
        if (!querySnapshot) return;
        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          ticketStatuses[data.userId] = data.status;
        });
      });

      if (!isCurrentGeneration()) return false;
      users.value = users.value.map((user) => {
        if (!Object.prototype.hasOwnProperty.call(ticketStatuses, user.uid)) return user;
        return {
          ...user,
          ticketStatus: ticketStatuses[user.uid] || null,
        };
      });
      return true;
    };

    const applyLoadedUsers = (list = []) => {
      const safeList = Array.isArray(list) ? list : [];
      const progressGeneration = ++programProgressGeneration;
      users.value = safeList;

      nextTick()
        .then(() => {
          if (progressGeneration !== programProgressGeneration) return;
          return refreshProgramProgress(progressGeneration);
        })
        .catch((error) => {
          if (progressGeneration !== programProgressGeneration) return;
          console.warn('Caricamento dettaglio programmi fallito:', error);
        });

      const immediateTicketUsers = safeList.slice(0, TICKET_STATUS_IMMEDIATE_LIMIT);
      const deferredTicketUsers = safeList.slice(TICKET_STATUS_IMMEDIATE_LIMIT);
      scheduleLazyTask(async () => {
        if (progressGeneration !== programProgressGeneration) return;
        try {
          const immediateLoaded = await loadTicketStatuses(
            immediateTicketUsers,
            progressGeneration
          );
          if (!immediateLoaded) return;
        } catch (error) {
          if (progressGeneration !== programProgressGeneration) return;
          console.warn('Caricamento stati ticket prioritari fallito:', error);
        }
        if (progressGeneration !== programProgressGeneration) return;
        if (deferredTicketUsers.length) {
          await loadTicketStatuses(deferredTicketUsers, progressGeneration);
        }
      });

      const noteCandidates = safeList.filter((user) => user?._expiringListNoteNeedsFetch);
      if (noteCandidates.length) {
        scheduleLazyTask(async () => {
          await loadExpiringListNotes(noteCandidates);
        });
      }

      const highlightCandidates = safeList.slice(0, HIGHLIGHT_PREFETCH_LIMIT);
      scheduleLazyTask(async () => {
        await prefetchUserHighlights(highlightCandidates);
      });
    };

    const mapIndexEntryToUser = (data = {}) => ({
      uid: String(data.uid || '').trim(),
      email: String(data.email || '').trim(),
      name: String(data.name || '').trim(),
      surname: String(data.surname || '').trim(),
      studio: String(data.studio || '').trim(),
      phone: resolveRawPhone(data),
      _nextExpiryMs: Number(data.nextExpiryMs) || 0,
      _nextOrderTime: Number(data.nextOrderTime) || 0,
      _nextDurationMonths: Number(data.nextDurationMonths) || 0,
      _lastExpiryMs: Number(data.lastExpiryMs) || 0,
      _lastOrderTime: Number(data.lastOrderTime) || 0,
      _lastDurationMonths: Number(data.lastDurationMonths) || 0,
      [EXPIRING_LIST_NOTE_FIELD]: String(data[EXPIRING_LIST_NOTE_FIELD] || ''),
      [EXPIRING_LIST_STOPPED_FIELD]: data[EXPIRING_LIST_STOPPED_FIELD] === true,
      [EXPIRING_LIST_STOPPED_AT_FIELD]: data[EXPIRING_LIST_STOPPED_AT_FIELD] || null,
      [EXPIRING_LIST_STOPPED_BY_FIELD]: data[EXPIRING_LIST_STOPPED_BY_FIELD] || null,
      operationalHold: data.operationalHold || null,
      operationalHoldActive: data.operationalHoldActive === true,
      operationalHoldReason: data.operationalHoldReason || '',
      operationalHoldNote: data.operationalHoldNote || '',
      operationalHoldHideFromOperationalLists: data.operationalHoldHideFromOperationalLists === true,
      operationalHoldSuppressNotifications: data.operationalHoldSuppressNotifications === true,
      _expiringListNoteNeedsFetch: true,
      ticketStatus: null,
    });

    const fetchExpiringUsersFromIndex = async () => {
      const [metaCollection, metaDocId] = USERS_EXPIRING_INDEX_META_PATH;
      const metaSnap = await getDoc(doc(db, metaCollection, metaDocId));
      if (!metaSnap.exists()) {
        return { users: [], meta: null, available: false };
      }

      const meta = metaSnap.data() || {};
      const runId = String(meta.activeRunId || '').trim();
      if (!runId) {
        return { users: [], meta, available: false };
      }

      const snap = await getDocs(
        query(
          collection(db, USERS_EXPIRING_INDEX_COLLECTION),
          where('runId', '==', runId)
        )
      );

      const indexedUsers = snap.docs
        .map((docSnap) => mapIndexEntryToUser(docSnap.data() || {}))
        .filter((user) => user.uid || normalizeEmail(user.email));
      const hydratedUsers = await hydrateExpiringListUserFields(indexedUsers);

      return {
        users: hydratedUsers,
        meta,
        available: true,
      };
    };

    const openInNewTab = (route) => {
      const resolved = router.resolve(route);
      window.open(resolved.href, '_blank', 'noopener');
    };

    const viewUserDetails = (userId) => {
      openInNewTab({ name: 'UserDetails', params: { userId: userId } });
    };

    const viewUserCommunications = (userId) => {
      openInNewTab({ name: 'UserComunicazioni', params: { userId: userId } });
    };

    const getWhatsAppPhone = (user) => normalizeWhatsAppPhone(resolveRawPhone(user));

    const hasWhatsAppPhone = (user) => !!getWhatsAppPhone(user);

    const whatsAppButtonTitle = (user) => {
      const rawPhone = resolveRawPhone(user);
      return rawPhone
        ? `Apri WhatsApp Web: ${rawPhone}`
        : 'Numero di telefono non disponibile';
    };

    const openWhatsAppChat = (user) => {
      const phone = getWhatsAppPhone(user);
      if (!phone) {
        alert('Numero di telefono non disponibile per questo utente.');
        return;
      }
      openWhatsAppWebWindow(`https://web.whatsapp.com/send?phone=${phone}`);
    };

    const fetchTicketIdForUser = async (userId) => {
      if (!userId) {
        console.error('Errore: userId non definito');
        return null;
      }

      try {
        const ticketsRef = collection(db, 'assistenzatecnica');
        const q = query(ticketsRef, where('userId', '==', userId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const ticketDoc = querySnapshot.docs[0];
          return ticketDoc.id;
        }
        return null;
      } catch (error) {
        console.error('Errore nel recupero del ticketId:', error);
        return null;
      }
    };

    const viewTechnicalConversation = async (userId) => {
      if (!userId) {
        console.error('Errore: userId non definito');
        return;
      }

      try {
        const ticketId = await fetchTicketIdForUser(userId);

        if (ticketId) {
          openInNewTab({
            name: 'UserConversationTecnica',
            params: { userId, ticketId },
          });
        } else {
          selectedUserId.value = userId;
          showCreateConversationModal.value = true;
        }
      } catch (error) {
        console.error('Errore durante il recupero della conversazione tecnica:', error);
      }
    };

    const closeCreateConversationModal = () => {
      showCreateConversationModal.value = false;
      newConversation.value = { title: '', message: '' };
      pendingConversation = null;
    };

    const createConversation = async () => {
      if (isCreatingConversation.value) return;
      const draft = newConversation.value;
      const userId = selectedUserId.value;
      const title = draft.title.trim();
      const content = draft.message.trim();
      if (!userId || !title || !content) {
        alert('Inserisci un titolo e un messaggio per la conversazione.');
        return;
      }
      const operatorId = getAuth().currentUser?.uid;
      if (!operatorId) {
        console.error("Errore: ID dell'operatore non trovato.");
        return;
      }
      const originalTitle = draft.title;
      const originalMessage = draft.message;
      const key = JSON.stringify([operatorId, userId, originalTitle, originalMessage]);
      if (pendingConversation?.key !== key) {
        pendingConversation = { key, payload: { userId, title, content, messageId: createTechnicalMessageId() } };
      }
      const request = pendingConversation;
      const isCurrentDraft = () => selectedUserId.value === userId && newConversation.value === draft
        && draft.title === originalTitle && draft.message === originalMessage;
      isCreatingConversation.value = true;
      try {
        const result = await createTechnicalTicket(request.payload);
        if (pendingConversation === request) pendingConversation = null;
        if (isCurrentDraft()) {
          alert(result.created === false ? 'Messaggio inviato nella conversazione esistente!' : 'Conversazione creata con successo!');
          closeCreateConversationModal();
        }
        return result;
      } catch (error) {
        console.error('Errore durante la creazione della conversazione:', error);
        if (isCurrentDraft()) alert('Errore durante la creazione della conversazione.');
      } finally {
        isCreatingConversation.value = false;
      }
    };

    const openDeliveryMessagesModal = () => {
      showFullMessage.value = deliveryMessages.value.map(() => false);
      showDeliveryMessagesModal.value = true;
    };

    const closeDeliveryMessagesModal = () => {
      showDeliveryMessagesModal.value = false;
    };

    const copyMessageToConversation = (content) => {
      const currentMessage = newConversation.value?.message;
      if (typeof currentMessage === 'string' && currentMessage.length > 0) {
        newConversation.value.message += '\n' + content;
      } else {
        newConversation.value.message = content;
      }

      closeDeliveryMessagesModal();
    };

    const truncateMessage = (message = '', index) => {
      if (showFullMessage.value[index]) {
        return message;
      }
      return message.length > 150 ? message.slice(0, 150) + '...' : message;
    };

    const toggleFullMessage = (index) => {
      showFullMessage.value[index] = !showFullMessage.value[index];
    };

    const formatDate = (value) => {
      if (!value) return '—';
      if (value && typeof value.toDate === 'function') {
        const date = value.toDate();
        return date.toLocaleDateString('it-IT');
      }
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? '—' : value.toLocaleDateString('it-IT');
      }
      if (typeof value === 'number') {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('it-IT');
      }
      if (typeof value === 'string') {
        const parsed = toDateSafe(value);
        return parsed ? parsed.toLocaleDateString('it-IT') : '—';
      }
      return '—';
    };

    const fetchExpiringUsers = async () => {
      isLoading.value = true;

      try {
        const indexedResult = await fetchExpiringUsersFromIndex();
        if (indexedResult.available) {
          applyLoadedUsers(indexedResult.users);
          return;
        }

        const usersMap = new Map();
        const expiringMetaByUid = new Map();
        const expiringMetaByEmail = new Map();
        const validOrders = [];
        const seenOrderIds = new Set();

        const pickFarthestExpiryInfo = (left, right) => {
          if (!left) return right || null;
          if (!right) return left;
          if ((left.expiryMs || 0) !== (right.expiryMs || 0)) {
            return (left.expiryMs || 0) > (right.expiryMs || 0) ? left : right;
          }
          return (left.orderTime || 0) >= (right.orderTime || 0) ? left : right;
        };

        const updateFarthestExpiryMeta = (map, key, info) => {
          if (!key || !info) return;
          const current = map.get(key);
          map.set(key, pickFarthestExpiryInfo(current, info));
        };

        let productIndex = null;
        try {
          productIndex = await buildProductsIndex(db);
        } catch (err) {
          console.warn('Impossibile caricare products per durata:', err);
        }

        const ordersRef = collection(db, 'orders');
        const appendOrderCandidate = (docSnap) => {
          if (!docSnap?.id || seenOrderIds.has(docSnap.id)) return;
          seenOrderIds.add(docSnap.id);
          const candidate = buildValidOrderCandidate(
            { id: docSnap.id, ...docSnap.data() },
            productIndex
          );
          if (candidate) validOrders.push(candidate);
        };

        const scanOrdersByDocumentId = async () => {
          let lastDoc = null;
          let hasMore = true;
          while (hasMore) {
            const q = lastDoc
              ? query(ordersRef, orderBy('__name__'), startAfter(lastDoc), limit(ORDER_PAGE_SIZE))
              : query(ordersRef, orderBy('__name__'), limit(ORDER_PAGE_SIZE));
            const snap = await getDocs(q);
            if (snap.empty) {
              hasMore = false;
              break;
            }
            snap.forEach(appendOrderCandidate);
            lastDoc = snap.docs[snap.docs.length - 1];
            await nextTick();
          }
        };

        const scanOrdersByRecentOrderDate = async (startOrderDateString) => {
          if (!startOrderDateString) return;
          let lastDoc = null;
          let hasMore = true;
          while (hasMore) {
            const constraints = [
              where('orderDate', '>=', startOrderDateString),
              orderBy('orderDate', 'desc'),
              limit(ORDER_PAGE_SIZE),
            ];
            if (lastDoc) {
              constraints.splice(2, 0, startAfter(lastDoc));
            }
            const snap = await getDocs(query(ordersRef, ...constraints));
            if (snap.empty) {
              hasMore = false;
              break;
            }
            snap.forEach(appendOrderCandidate);
            lastDoc = snap.docs[snap.docs.length - 1];
            await nextTick();
          }
        };

        const lookbackStartDate = resolveOrderLookbackStartDate(productIndex);
        const lookbackStartOrderDate = toOrderDateQueryString(lookbackStartDate);
        let optimizedScanCompleted = false;
        if (lookbackStartOrderDate) {
          try {
            await scanOrdersByRecentOrderDate(lookbackStartOrderDate);
            optimizedScanCompleted = true;
          } catch (rangeError) {
            console.warn('Query ordini ottimizzata non disponibile, fallback completo:', rangeError);
            validOrders.length = 0;
            seenOrderIds.clear();
          }
        }
        if (!optimizedScanCompleted) {
          await scanOrdersByDocumentId();
        }

        const now = Date.now();
        const identityStats = buildOrderIdentityStats(validOrders);
        const firstOrderIndexes = identityStats.firstOrderIndexes;
        const firstDeliveryLookupWindowMs =
          (MAX_WINDOW_DAYS + FIRST_DELIVERY_LOOKUP_BUFFER_DAYS) * 24 * 60 * 60 * 1000;
        const firstOrderRefs = Array.from(firstOrderIndexes)
          .filter((idx) => {
            const order = validOrders[idx];
            if (!order) return false;
            const root = identityStats.rootByIndex[idx];
            const count = identityStats.orderCountByRoot.get(root) || 0;
            const maxOrderTime = identityStats.maxOrderTimeByRoot.get(root) || 0;
            if (count > 1 && order.orderTime < maxOrderTime) return false;
            const fallbackExpiryDate = addMonths(new Date(order.orderTime), order.durationMonths);
            if (!fallbackExpiryDate) return false;
            const diff = fallbackExpiryDate.getTime() - now;
            return diff >= -firstDeliveryLookupWindowMs && diff <= firstDeliveryLookupWindowMs;
          })
          .flatMap((idx) => {
          const refs = validOrders[idx]?.orderRefs;
          return Array.isArray(refs) ? refs : [];
          });
        const firstDeliveryByRef = await fetchFirstDeliveryDoneAtByOrderRef(db, firstOrderRefs);

        validOrders.forEach((order, idx) => {
          let baseTimeMs = order.orderTime;
          if (firstOrderIndexes.has(idx)) {
            const firstDeliveryMs = resolveFirstDeliveryForOrder(order, firstDeliveryByRef);
            if (firstDeliveryMs) {
              baseTimeMs = firstDeliveryMs;
            }
          }

          const expiryDate = addMonths(new Date(baseTimeMs), order.durationMonths);
          if (!expiryDate) return;
          const expiryMs = expiryDate.getTime();
          if (!Number.isFinite(expiryMs) || expiryMs <= 0) return;

          const metaInfo = {
            orderTime: order.orderTime,
            durationMonths: order.durationMonths,
            expiryMs,
          };

          order.emails.forEach((email) => {
            updateFarthestExpiryMeta(expiringMetaByEmail, email, metaInfo);
          });
          order.uids.forEach((uid) => {
            updateFarthestExpiryMeta(expiringMetaByUid, uid, metaInfo);
          });
        });

        const maxWindowMs = MAX_WINDOW_DAYS * 24 * 60 * 60 * 1000;
        const isWithinSupersetWindow = (info) => {
          if (!info?.expiryMs) return false;
          const diff = info.expiryMs - now;
          return diff >= -maxWindowMs && diff <= maxWindowMs;
        };
        const uids = Array.from(expiringMetaByUid.entries())
          .filter(([, info]) => isWithinSupersetWindow(info))
          .map(([uid]) => uid);
        const emails = Array.from(expiringMetaByEmail.entries())
          .filter(([, info]) => isWithinSupersetWindow(info))
          .map(([email]) => email);

        if (uids.length) {
          const uidChunks = chunkList(uids, USER_CHUNK_SIZE);
          const snaps = await Promise.all(uidChunks.map((chunk) =>
            getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)))
          ));
          snaps.forEach((snap) => {
            snap.forEach((docSnap) => {
              if (!usersMap.has(docSnap.id)) {
                usersMap.set(docSnap.id, { uid: docSnap.id, ...docSnap.data() });
              }
            });
          });
        }

        if (emails.length) {
          const emailChunks = chunkList(emails, USER_CHUNK_SIZE);
          const snaps = await Promise.all(emailChunks.map((chunk) =>
            getDocs(query(collection(db, 'users'), where('email', 'in', chunk)))
          ));
          snaps.forEach((snap) => {
            snap.forEach((docSnap) => {
              if (!usersMap.has(docSnap.id)) {
                usersMap.set(docSnap.id, { uid: docSnap.id, ...docSnap.data() });
              }
            });
          });
        }

        const resolveUserExpiringInfo = (user) => {
          const uidKey = user?.uid || '';
          const emailKey = normalizeEmail(user?.email);
          const uidInfo = uidKey ? expiringMetaByUid.get(uidKey) : null;
          const emailInfo = emailKey ? expiringMetaByEmail.get(emailKey) : null;
          return pickFarthestExpiryInfo(uidInfo, emailInfo);
        };

        const list = Array.from(usersMap.values())
          .map((user) => {
            const info = resolveUserExpiringInfo(user);
            const expiryMs = info?.expiryMs || 0;
            const orderTime = info?.orderTime || 0;
            const durationMonths = info?.durationMonths || 0;
            const isFuture = !!expiryMs && expiryMs >= now;
            return {
              ...user,
              _nextExpiryMs: isFuture ? expiryMs : 0,
              _nextOrderTime: isFuture ? orderTime : 0,
              _nextDurationMonths: isFuture ? durationMonths : 0,
              _lastExpiryMs: isFuture ? 0 : expiryMs,
              _lastOrderTime: isFuture ? 0 : orderTime,
              _lastDurationMonths: isFuture ? 0 : durationMonths,
              [EXPIRING_LIST_NOTE_FIELD]: String(user?.[EXPIRING_LIST_NOTE_FIELD] || ''),
              [EXPIRING_LIST_STOPPED_FIELD]: user?.[EXPIRING_LIST_STOPPED_FIELD] === true,
              [EXPIRING_LIST_STOPPED_AT_FIELD]: user?.[EXPIRING_LIST_STOPPED_AT_FIELD] || null,
              [EXPIRING_LIST_STOPPED_BY_FIELD]: user?.[EXPIRING_LIST_STOPPED_BY_FIELD] || null,
              operationalHold: user?.operationalHold || null,
              operationalHoldActive: user?.operationalHoldActive === true,
              operationalHoldReason: user?.operationalHoldReason || '',
              operationalHoldNote: user?.operationalHoldNote || '',
              operationalHoldHideFromOperationalLists: user?.operationalHoldHideFromOperationalLists === true,
              operationalHoldSuppressNotifications: user?.operationalHoldSuppressNotifications === true,
              _expiringListNoteNeedsFetch: false,
            };
          });

        applyLoadedUsers(list);
      } catch (error) {
        console.error('Errore nel caricamento degli utenti TOP:', error);
        users.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const refreshExpiringIndexManual = async () => {
      if (isIndexRefreshing.value) return;
      isIndexRefreshing.value = true;
      try {
        const callable = httpsCallable(functions, 'refreshUsersExpiringIndex');
        await callable({
          reason: 'manual_button',
          source: 'users-expiring-list',
        });
        await fetchExpiringUsers();
      } catch (error) {
        console.error('Errore refresh manuale indice scadenze:', error);
        const detailsMessage = error?.details?.message || error?.message || '';
        if (detailsMessage) {
          console.error('Dettaglio errore refresh indice:', detailsMessage);
        }
        alert('Errore durante il refresh manuale dell\'indice.');
      } finally {
        isIndexRefreshing.value = false;
      }
    };

    onMounted(fetchExpiringUsers);

    const filteredUsers = computed(() => {
      const now = Date.now();
      const windowDays = Number(expiringWindowDays.value) || DEFAULT_WINDOW_DAYS;
      const windowMs = windowDays * 24 * 60 * 60 * 1000;

      const withDisplay = users.value.map((user) => {
        const hasNext = user._nextExpiryMs && user._nextExpiryMs >= now;
        const hasLast = user._lastExpiryMs && user._lastExpiryMs < now;
        let displayExpiryMs = 0;
        let displayOrderTime = 0;
        let displayDurationMonths = 0;

        if (showStoppedOnly.value) {
          if (hasNext && user._nextExpiryMs <= now + windowMs) {
            displayExpiryMs = user._nextExpiryMs;
            displayOrderTime = user._nextOrderTime;
            displayDurationMonths = user._nextDurationMonths;
          } else if (hasLast && user._lastExpiryMs >= now - windowMs) {
            displayExpiryMs = user._lastExpiryMs;
            displayOrderTime = user._lastOrderTime;
            displayDurationMonths = user._lastDurationMonths;
          } else {
            return { ...user, _expiringExpiryMs: 0 };
          }
        } else if (showExpiredOnly.value) {
          if (hasNext) return { ...user, _expiringExpiryMs: 0 };
          if (!hasLast) return { ...user, _expiringExpiryMs: 0 };
          if (user._lastExpiryMs < now - windowMs) return { ...user, _expiringExpiryMs: 0 };
          displayExpiryMs = user._lastExpiryMs;
          displayOrderTime = user._lastOrderTime;
          displayDurationMonths = user._lastDurationMonths;
        } else if (hasNext) {
          if (user._nextExpiryMs > now + windowMs) return { ...user, _expiringExpiryMs: 0 };
          displayExpiryMs = user._nextExpiryMs;
          displayOrderTime = user._nextOrderTime;
          displayDurationMonths = user._nextDurationMonths;
        }

        return {
          ...user,
          _expiringExpiryMs: displayExpiryMs,
          _expiringOrderTime: displayOrderTime,
          _expiringDurationMonths: displayDurationMonths,
        };
      });

      const filtered = withDisplay.filter((user) => user._expiringExpiryMs);
      const filteredByStop = filtered.filter((user) => {
        const isStopped = isExpiringListStopped(user);
        const isTransitioning = stopTransitionFlags[user.uid] === true;
        if (showStoppedOnly.value) return isStopped || isTransitioning;
        return !isStopped || isTransitioning;
      });
      const studio = studioFilter.value;
      const filteredByStudio = filteredByStop.filter((user) => {
        if (!studio) return true;
        const normalized = normalizeStudioName(user.studio || '');
        if (studio === NO_STUDIO_FILTER) return !normalized;
        return normalizeStudioName(studio) === normalized;
      });

      return filteredByStudio
        .slice()
        .sort((a, b) => (a._expiringExpiryMs || 0) - (b._expiringExpiryMs || 0));
    });

    const programProgressEntries = (user) => user?._programProgress?.entries || [{
      key: 'pending', label: programProgressError.value ? 'Da verificare' : '…',
      state: programProgressError.value ? 'review' : 'loading',
      summary: programProgressError.value ? 'Avanzamento non caricato' : 'Caricamento del programma corrente…',
      details: programProgressError.value ? ['Riprova il caricamento degli avanzamenti.'] : [],
    }];

    return {
      users,
      filteredUsers,
      isLoading,
      viewUserDetails,
      viewUserCommunications,
      viewTechnicalConversation,
      openWhatsAppChat,
      hasWhatsAppPhone,
      whatsAppButtonTitle,
      showCreateConversationModal,
      newConversation,
      isCreatingConversation,
      createConversation,
      closeCreateConversationModal,
      openDeliveryMessagesModal,
      closeDeliveryMessagesModal,
      showDeliveryMessagesModal,
      deliveryMessages,
      showFullMessage,
      copyMessageToConversation,
      truncateMessage,
      toggleFullMessage,
      formatDate,
      orderSummary,
      showOrderModal,
      orderLoading,
      orderError,
      selectedOrder,
      productHighlightClass,
      showLastOrder,
      closeOrderModal,
      orderBadgeStatus,
      showExpiredOnly,
      showStoppedOnly,
      toggleExpiredOnly,
      toggleStoppedOnly,
      expiringWindowDays,
      studioFilter,
      studioOptions: STUDIO_OPTIONS,
      noStudioFilterValue: NO_STUDIO_FILTER,
      isIndexRefreshing,
      refreshExpiringIndexManual,
      stopSavingFlags,
      stopTransitionFlags,
      isUserOperationallyHeld,
      isExpiringListStopped,
      expiringListStopButtonTitle,
      toggleExpiringListStopped,
      programProgressEntries,
      programProgressLoading,
      programProgressError,
      programProgressUpdatedAt,
      refreshProgramProgress,
    };
  },
};
</script>

<style scoped>
.search-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  margin: 24px 0;
  padding: 18px 20px;
  border-radius: 20px;
  border: 1px solid #dbe2f0;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.search-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;
  flex: 1 1 240px;
}

.search-bar {
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  padding: 10px 14px;
  font-size: 0.95rem;
  color: #0f172a;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.search-bar:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.65);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.search-container > button {
  flex: 0 0 auto;
}

.top-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}

.expiring-select {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 600;
}

.expiring-select label {
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.expiring-select select {
  border: none;
  background: transparent;
  font-weight: 600;
  color: inherit;
  outline: none;
  cursor: pointer;
}

.create-user-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(120deg, #22c55e, #16a34a);
  color: #ffffff;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(34, 197, 94, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.create-user-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 34px rgba(34, 197, 94, 0.35);
  filter: saturate(1.1);
}

.create-user-button--active {
  background: linear-gradient(120deg, #2563eb, #4338ca);
  box-shadow: 0 12px 28px rgba(37, 99, 235, 0.3);
}

.create-user-button--refresh {
  background: linear-gradient(120deg, #0ea5e9, #0284c7);
  box-shadow: 0 12px 28px rgba(2, 132, 199, 0.28);
}

.create-user-button--stop-filter {
  background: linear-gradient(120deg, #ef4444, #dc2626);
  box-shadow: 0 12px 28px rgba(220, 38, 38, 0.26);
}

.create-user-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.spin-icon {
  animation: spin-icon 1s linear infinite;
}

@keyframes spin-icon {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.minimal-loader {
  margin-left: 12px;
  font-size: 0.9rem;
  color: #2563eb;
  letter-spacing: 0.02em;
}

.tickets {
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;
}

.users-table {
  width: 100%;
  min-width: 2280px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.95rem;
  color: #0f172a;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  overflow: visible;
  table-layout: fixed;
}

.users-table thead th {
  position: sticky;
  top: 0;
  z-index: 6;
  background: linear-gradient(180deg, #f7f9fc 0%, #eef2f7 100%);
  padding: 14px 18px;
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0f172a;
  border-bottom: 1px solid #dbe2f0;
  white-space: nowrap;
}

.users-table thead th:last-child,
.users-table tbody td:last-child {
  text-align: center;
}

.users-table tbody tr {
  --row-bg: #ffffff;
  transition: background-color 0.2s ease;
}

.users-table tbody tr:nth-child(even) {
  --row-bg: #f8fafc;
}

.users-table tbody tr:hover {
  --row-bg: #eef6ff;
}

.users-table tbody tr.row--stopped {
  --row-bg: #f1f5f9;
}

.users-table tbody tr.row--stop-transition {
  --row-bg: #e5e7eb;
}

.users-table tbody tr.row--stop-transition td {
  color: #64748b;
}

.users-table tbody td {
  padding: 14px 18px;
  border-bottom: 1px solid #e4e9f2;
  vertical-align: middle;
  color: #0f172a;
  background: var(--row-bg);
  white-space: nowrap;
  word-break: normal;
}

.actions-cell {
  text-align: center;
  white-space: nowrap;
}

.note-cell {
  min-width: 0;
  width: 300px;
}

.note-field {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: rgba(255, 255, 255, 0.88);
}

.note-input {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-height: 38px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.38);
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  font-size: 0.88rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.note-input::placeholder {
  color: #94a3b8;
}

.note-input:focus {
  outline: none;
  border-color: rgba(59, 130, 246, 0.58);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14);
  background: #ffffff;
}

.note-save-status {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: #64748b;
}

.users-table .email-column {
  width: 320px;
}

.users-table tbody .email-cell {
  width: 320px;
  max-width: 320px;
  min-width: 0;
}

.email-text {
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.users-table .order-column {
  width: 90px;
  text-align: center;
}

.users-table .order-date-column,
.users-table .expiry-column {
  width: 150px;
}

.users-table .duration-column {
  width: 120px;
  text-align: center;
}

.order-date-cell,
.duration-cell,
.expiry-cell {
  overflow: hidden;
  text-overflow: ellipsis;
}

.duration-cell {
  text-align: center;
}

.users-table .program-progress-column {
  width: 120px;
  text-align: center;
}

.program-progress-cell {
  position: relative;
  overflow: visible;
  text-align: center;
}

.program-progress-tooltip-wrap {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin: 5px 3px;
  outline: none;
}

.program-progress-state { font-size: 11px; color: #617083; line-height: 1.35; white-space: normal; }
.programme-alignment-info { margin: 10px 0 18px; padding: 14px 18px; border: 1px solid #dce3e9; border-radius: 12px; background: #f7f9fb; color: #596978; font-size: 13px; line-height: 1.6; }
.programme-alignment-info p { margin: 0; }
.programme-alignment-info p + p { margin-top: 5px; }
.programme-alignment-info a { color: #355f80; text-decoration: underline; }
.programme-progress-loading { margin: 12px 0; color: #375b46; font-size: 13px; }
.programme-progress-track { height: 6px; margin-top: 9px; border-radius: 8px; overflow: hidden; background: #dcebe1; }
.programme-progress-track > span { display: block; height: 100%; width: 35%; background: #21834c; border-radius: inherit; animation: programme-progress-slide 1.6s ease-in-out infinite alternate; }
@keyframes programme-progress-slide { from { transform: translateX(-100%); } to { transform: translateX(285%); } }
@media (prefers-reduced-motion: reduce) { .programme-progress-track > span { width: 100%; animation: none; } }
.programme-progress-error { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; padding: 14px 18px; margin-bottom: 16px; background: #fff5f3; border: 1px solid #ecc9c3; border-radius: 12px; color: #933f38; font-size: 13px; }
.programme-progress-error button { min-height: 40px; padding: 8px 12px; background: #fff; border: 1px solid #d7ada6; border-radius: 8px; color: inherit; cursor: pointer; }

.program-progress-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  min-height: 32px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.36);
  background: #f8fafc;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  line-height: 1;
}

.program-progress-tooltip {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 12px);
  z-index: 30;
  display: flex;
  flex-direction: column;
  gap: 7px;
  width: 360px;
  max-width: min(360px, 80vw);
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.96);
  color: #f8fafc;
  box-shadow: 0 22px 50px rgba(15, 23, 42, 0.28);
  font-size: 0.78rem;
  line-height: 1.35;
  text-align: left;
  white-space: normal;
  transform: translate(-50%, 8px);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.program-progress-tooltip::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -7px;
  width: 14px;
  height: 14px;
  background: rgba(15, 23, 42, 0.96);
  border-right: 1px solid rgba(148, 163, 184, 0.35);
  border-bottom: 1px solid rgba(148, 163, 184, 0.35);
  transform: translateX(-50%) rotate(45deg);
}

.program-progress-tooltip strong {
  display: block;
  margin-bottom: 2px;
  color: #ffffff;
  font-size: 0.86rem;
  letter-spacing: 0.01em;
}

.program-progress-tooltip span {
  display: block;
  color: #dbeafe;
}

.program-progress-tooltip-wrap:hover .program-progress-tooltip,
.program-progress-tooltip-wrap:focus-within .program-progress-tooltip {
  opacity: 1;
  transform: translate(-50%, 0);
}

.program-progress-chip--complete {
  border-color: rgba(34, 197, 94, 0.28);
  background: #dcfce7;
  color: #166534;
}

.program-progress-chip--draft {
  border-color: rgba(245, 158, 11, 0.32);
  background: #fef3c7;
  color: #92400e;
}

.program-progress-chip--current,
.program-progress-chip--waiting {
  border-color: rgba(148, 163, 184, 0.36);
  background: #f1f5f9;
  color: #475569;
}

.program-progress-chip--not-started { background: #fbe1de; border-color: #e4aca6; color: #a02b2c; }
.program-progress-chip--hold { background: #ece9f4; border-color: #cfc4e6; color: #625080; }
.program-progress-chip--review { background: #fff5dc; border-color: #e6d3a3; color: #805e21; }

.program-progress-chip--loading {
  background: #eef2ff;
  color: #4338ca;
}

.users-table .name-column,
.users-table .surname-column {
  width: 220px;
}

.users-table .studio-column {
  width: 280px;
}

.name-cell,
.surname-cell,
.studio-cell {
  overflow: hidden;
  text-overflow: ellipsis;
}

.users-table .note-column {
  width: 320px;
}

.users-table .actions-column {
  width: 270px;
}

.order-cell {
  text-align: center;
}

.order-badge {
  width: 34px;
  height: 34px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: #f7f9fc;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.order-badge .material-icons {
  font-size: 20px;
}

.order-badge:hover {
  background: #e0e7ff;
  border-color: rgba(99, 102, 241, 0.55);
  color: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.12);
}

.order-badge--green {
  border: none;
  background: linear-gradient(120deg, #22c55e, #16a34a);
  color: #ffffff;
  box-shadow: 0 16px 34px rgba(34, 197, 94, 0.32);
}

.order-badge--green:hover {
  background: linear-gradient(120deg, #22c55e, #16a34a);
  border: none;
  box-shadow: 0 18px 40px rgba(34, 197, 94, 0.36);
  color: #ffffff;
}

.order-badge--orange {
  border: none;
  background: linear-gradient(120deg, #f97316, #ea580c);
  color: #ffffff;
  box-shadow: 0 16px 34px rgba(249, 115, 22, 0.28);
}

.order-badge--orange:hover {
  background: linear-gradient(120deg, #f97316, #ea580c);
  border: none;
  box-shadow: 0 18px 40px rgba(234, 88, 12, 0.32);
  color: #ffffff;
}

.order-badge--neutral {
  background: #f8fafc;
  border-color: rgba(148, 163, 184, 0.45);
}

.buttons-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.buttons-container button {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: #f7f9fc;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, color 0.2s ease;
}

.buttons-container button:hover {
  background: #e0e7ff;
  border-color: rgba(99, 102, 241, 0.6);
  color: #4338ca;
  transform: translateY(-1px);
}

.buttons-container button:disabled {
  cursor: not-allowed;
  opacity: 0.42;
  transform: none;
}

.buttons-container button:disabled:hover {
  background: #f7f9fc;
  border-color: rgba(148, 163, 184, 0.3);
  color: #475569;
}

.buttons-container .whatsapp-button {
  background: #dcfce7;
  border-color: rgba(34, 197, 94, 0.36);
  color: #15803d;
}

.buttons-container .whatsapp-button:hover {
  background: #bbf7d0;
  border-color: rgba(22, 163, 74, 0.58);
  color: #166534;
}

.buttons-container .whatsapp-button:disabled,
.buttons-container .whatsapp-button:disabled:hover {
  background: #f1f5f9;
  border-color: rgba(148, 163, 184, 0.3);
  color: #64748b;
}

.buttons-container .stop-button {
  background: #fee2e2;
  border-color: rgba(239, 68, 68, 0.42);
  color: #dc2626;
}

.buttons-container .stop-button:hover {
  background: #fecaca;
  border-color: rgba(220, 38, 38, 0.64);
  color: #b91c1c;
}

.buttons-container .stop-button--restore {
  background: #f1f5f9;
  border-color: rgba(100, 116, 139, 0.34);
  color: #475569;
}

.buttons-container .stop-button--restore:hover {
  background: #e2e8f0;
  border-color: rgba(71, 85, 105, 0.44);
  color: #334155;
}

.operational-hold-dot {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 3px 8px;
  border-radius: 999px;
  background: #fee2e2;
  color: #991b1b;
  font-size: 11px;
  font-weight: 800;
}

.buttons-container .material-icons {
  font-size: 20px;
}

.buttons-container .whatsapp-icon {
  display: block;
  width: 21px;
  height: 21px;
}

.status-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.05);
}

.status-dot.orange {
  background-color: #f59e0b;
}

.status-dot.green {
  background-color: #22c55e;
}

.status-dot.yellow {
  background-color: #facc15;
}

.modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.55);
  z-index: 2000;
  width: 100%;
}

.modal-content {
  position: relative;
  width: min(540px, 92vw);
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 24px;
  padding: 32px 36px;
  background: linear-gradient(180deg, #ffffff 0%, #f5f7fb 100%);
  box-shadow: 0 32px 70px rgba(15, 23, 42, 0.2);
  color: #0f172a;
}

.modal-content h2 {
  margin: 0 0 18px;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color:#0f172a
}

.modal-content form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.modal-content form > div {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modal-content label {
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #475569;
}

.modal-content input,
.modal-content select,
.modal-content textarea {
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  padding: 12px 14px;
  font-size: 0.96rem;
  color: #0f172a;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.modal-content textarea {
  min-height: 160px;
  resize: vertical;
  line-height: 1.6;
}

.modal-content input:focus,
.modal-content select:focus,
.modal-content textarea:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.65);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.close {
  position: absolute;
  top: 18px;
  right: 18px;
  font-size: 1.4rem;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease;
}

.close:hover {
  color: #64748b;
  transform: scale(1.05);
}

.order-modal .close {
  color: #e2e8f0;
}

.modal-content button[type="button"] {
  align-self: flex-start;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.4);
  background: #f5f7ff;
  color: #4338ca;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(99, 102, 241, 0.16);
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.modal-content button[type="button"]:hover {
  background: #eef2ff;
  transform: translateY(-1px);
  box-shadow: 0 14px 30px rgba(99, 102, 241, 0.22);
}

.modal-content button[type="submit"] {
  width: 100%;
  padding: 12px 18px;
  border-radius: 16px;
  border: none;
  background: linear-gradient(120deg, #2563eb, #4338ca);
  color: #ffffff;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  box-shadow: 0 20px 38px rgba(37, 99, 235, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.modal-content button[type="submit"]:hover {
  transform: translateY(-1px);
  box-shadow: 0 26px 46px rgba(37, 99, 235, 0.35);
}

.message-section {
  margin-top: 18px;
  padding: 18px 20px;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-section h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
}

.message-section pre {
  margin: 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.35);
  font-size: 0.92rem;
  line-height: 1.6;
  color: #0f172a;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 260px;
  overflow: auto;
}

.message-section pre::-webkit-scrollbar {
  width: 8px;
}

.message-section pre::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.45);
  border-radius: 999px;
}

.message-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.message-actions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: #f1f5f9;
  color: #1f2937;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.message-actions button:hover {
  background: #e0e7ff;
  border-color: rgba(99, 102, 241, 0.5);
  color: #4338ca;
  transform: translateY(-1px);
}

.message-actions .copy-button {
  border: none;
  background: linear-gradient(120deg, #22c55e, #16a34a);
  color: #ffffff;
  box-shadow: 0 12px 28px rgba(34, 197, 94, 0.25);
}

.message-actions .copy-button:hover {
  box-shadow: 0 16px 36px rgba(34, 197, 94, 0.32);
}

.order-modal {
  width: min(360px, 92vw);
  padding: 30px 32px 34px;
  background: linear-gradient(160deg, #2f3543 0%, #1f262f 100%);
  border-radius: 26px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.45);
  color: #f8fafc;
  text-align: center;
}

.order-modal h2 {
  margin: 0 auto 20px;
  font-size: 1.45rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.order-loader,
.order-empty,
.order-error {
  font-size: 0.95rem;
  color: #e2e8f0;
  margin: 24px 0 0;
}

.order-error {
  color: #fca5a5;
}

.order-card {
  background: linear-gradient(165deg, #f8fafc 0%, #e5ebf6 100%);
  border-radius: 22px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
  padding: 22px 24px;
  color: #0f172a;
}

.order-card__body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.order-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
}

.order-field__label {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #4b5563;
}

.order-field__value {
  font-size: 1.02rem;
  font-weight: 700;
  color: #111827;
}

.order-field--products {
  border-top: 1px solid rgba(79, 70, 229, 0.16);
  padding-top: 16px;
  width: 100%;
}

.order-products {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  color: #1f2937;
  font-weight: 600;
}

.order-products li {
  padding: 6px 0;
  border-radius: 10px;
  text-align: center;
}

.order-product--green {
  color: #166534;
}

.order-product--orange {
  color: #9a3412;
}

.order-product--neutral {
  color: #1f2937;
}

@media (max-width: 1024px) {
  .top-actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .search-container {
    padding: 16px;
    gap: 12px;
  }

  .users-table {
    min-width: 2230px;
  }
}

@media (max-width: 640px) {
  .search-container {
    flex-direction: column;
    align-items: stretch;
  }

  .search-container > button {
    width: 100%;
  }

  .modal-content {
    padding: 28px 22px;
  }

  .message-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .message-actions button,
  .message-actions .copy-button {
    width: 100%;
  }
}
</style>
